import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Timer, ChevronLeft, ChevronRight, Send, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import PrismLogo from "@/components/PrismLogo";
import MathText from "@/components/MathText";

function formatTime(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function ExamTake() {
  const { examId } = useParams();
  const nav = useNavigate();

  const [phase, setPhase] = useState("intro"); // intro | running | submitting | result
  const [exam, setExam] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({}); // qid -> {selected_index, text_answer}
  const [current, setCurrent] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submittedRef = useRef(false);

  // Load exam info
  useEffect(() => {
    api
      .get(`/exams/${examId}`)
      .then((r) => setExam(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, [examId]);

  const doSubmit = useCallback(async () => {
    if (submittedRef.current || !attempt) return;
    submittedRef.current = true;
    setBusy(true);
    setPhase("submitting");
    try {
      const payload = {
        attempt_id: attempt.id,
        answers: Object.entries(answers).map(([question_id, v]) => ({
          question_id,
          selected_index: v.selected_index ?? null,
          text_answer: v.text_answer ?? null,
        })),
      };
      const { data } = await api.post("/attempts/submit", payload);
      setResult(data);
      setPhase("result");
    } catch (e) {
      setErr(formatApiError(e));
      setPhase("running");
      submittedRef.current = false;
    } finally {
      setBusy(false);
    }
  }, [attempt, answers]);

  // Timer
  useEffect(() => {
    if (phase !== "running") return;
    const iv = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(iv);
          // Auto-submit when time expires
          doSubmit();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [phase, doSubmit]);

  const start = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/attempts/start", { exam_id: examId });
      setAttempt(data.attempt);
      setExam(data.exam);
      setRemaining(data.exam.duration_min * 60);
      setPhase("running");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const questions = exam?.questions || [];
  const q = questions[current];

  const setAnswer = (qid, patch) =>
    setAnswers((a) => ({ ...a, [qid]: { ...(a[qid] || {}), ...patch } }));

  const answeredCount = useMemo(() => {
    return questions.filter((qq) => {
      const a = answers[qq.id];
      if (!a) return false;
      if (qq.type === "mcq") return a.selected_index !== undefined && a.selected_index !== null;
      return (a.text_answer || "").trim().length > 0;
    }).length;
  }, [answers, questions]);

  // ---------- Intro screen ----------
  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="exam-intro">
        <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PrismLogo size={36} />
            <span className="font-display text-xl font-extrabold text-slate-900">Prismiq</span>
          </div>
          <button onClick={() => nav("/dashboard/exams")} className="btn-ghost text-sm" data-testid="intro-back">
            Back
          </button>
        </header>
        <div className="max-w-2xl mx-auto px-6 py-16">
          {!exam ? (
            <p className="text-slate-500" data-testid="exam-loading">Loading exam…</p>
          ) : (
            <div className="qs-card p-8">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">{exam.subject}</p>
              <h1 className="font-display text-3xl sm:text-4xl font-black text-slate-900 mt-3" data-testid="intro-title">
                {exam.title}
              </h1>
              <p className="mt-4 text-slate-600 leading-relaxed">{exam.description}</p>
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Duration</p>
                  <p className="font-display text-xl font-bold text-slate-900 mt-1">{exam.duration_min} min</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Questions</p>
                  <p className="font-display text-xl font-bold text-slate-900 mt-1">{exam.questions?.length ?? 0}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Passing</p>
                  <p className="font-display text-xl font-bold text-slate-900 mt-1">{exam.passing_score}%</p>
                </div>
              </div>
              <div className="mt-8 flex items-center gap-3">
                <button onClick={start} disabled={busy} className="btn-primary" data-testid="start-attempt-button">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Timer className="w-4 h-4" />}
                  {busy ? "Starting…" : "Begin exam"}
                </button>
                <button onClick={() => nav("/dashboard/exams")} className="btn-ghost" data-testid="intro-cancel">
                  Cancel
                </button>
              </div>
              {err && <p className="mt-4 text-sm text-rose-600" data-testid="intro-error">{err}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- Result screen ----------
  if (phase === "result" && result) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="exam-result">
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="qs-card p-8 text-center">
            <div className={`w-16 h-16 mx-auto rounded-2xl grid place-items-center ${result.passed ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
              {result.passed ? <CheckCircle2 className="w-8 h-8" /> : <XCircle className="w-8 h-8" />}
            </div>
            <h2 className="font-display text-3xl font-black text-slate-900 mt-5" data-testid="result-heading">
              {result.passed ? "Nicely done!" : "So close — keep going!"}
            </h2>
            <p className="text-slate-500 text-sm mt-1" data-testid="result-exam-title">{exam?.title}</p>
            <p className="text-slate-600 mt-2">Your instant score is ready.</p>

            <div className="mt-8 grid grid-cols-3 gap-3">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Score</p>
                <p className="font-display text-3xl font-black text-slate-900 mt-1" data-testid="result-score">{result.score_percent}%</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Points</p>
                <p className="font-display text-3xl font-black text-slate-900 mt-1">
                  {result.score}<span className="text-base text-slate-500">/{result.total_points}</span>
                </p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Pass mark</p>
                <p className="font-display text-3xl font-black text-slate-900 mt-1">{result.passing_score}%</p>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-center gap-3">
              <button onClick={() => nav("/dashboard")} className="btn-primary" data-testid="result-back-dash">
                Back to dashboard
              </button>
              <button onClick={() => nav("/dashboard/results")} className="btn-ghost" data-testid="result-view-history">
                View all results
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Running screen ----------
  const timerCritical = remaining <= 60;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="exam-running">
      {/* Sticky top with timer */}
      <header className="sticky top-0 z-40 h-16 bg-white/85 backdrop-blur border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <PrismLogo size={36} />
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-indigo-600">Live exam</p>
            <p className="font-semibold text-sm text-slate-800 truncate max-w-[240px] sm:max-w-md">{exam?.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:block text-right">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Answered</p>
            <p className="font-semibold text-sm text-slate-800">{answeredCount}/{questions.length}</p>
          </div>
          <div className={`px-3 py-1.5 rounded-xl bg-slate-900 text-white font-display text-xl font-bold ${timerCritical ? "timer-critical" : ""}`} data-testid="exam-timer">
            <span className="inline-flex items-center gap-2"><Timer className="w-4 h-4" /> {formatTime(remaining)}</span>
          </div>
          <button onClick={doSubmit} disabled={busy} className="btn-primary text-sm" data-testid="submit-exam-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Submit
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-12 gap-6">
        {/* Question navigator */}
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <div className="qs-card p-4 sticky top-24" data-testid="question-navigator">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">Questions</p>
            <div className="grid grid-cols-5 gap-2">
              {questions.map((qq, i) => {
                const isCurrent = i === current;
                const answered = !!answers[qq.id] &&
                  (qq.type === "mcq"
                    ? answers[qq.id].selected_index !== undefined && answers[qq.id].selected_index !== null
                    : (answers[qq.id].text_answer || "").trim().length > 0);
                return (
                  <button
                    key={qq.id}
                    onClick={() => setCurrent(i)}
                    className={`aspect-square rounded-lg text-sm font-bold transition ${
                      isCurrent
                        ? "bg-indigo-600 text-white shadow"
                        : answered
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
                    }`}
                    data-testid={`q-nav-${i}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 space-y-1.5 text-[11px] text-slate-500">
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-indigo-600" /> Current</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200" /> Answered</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-slate-100 border border-slate-200" /> Unanswered</div>
            </div>
          </div>
        </aside>

        {/* Question panel */}
        <main className="lg:col-span-9 order-1 lg:order-2">
          <div className="qs-card p-6 sm:p-8" data-testid="question-panel">
            {q && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">
                    Question {current + 1} of {questions.length}
                  </p>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {q.type === "mcq" ? "Multiple choice" : "Descriptive"} · {q.points || 1} pt
                  </span>
                </div>
                <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-4 leading-snug" data-testid="question-text">
                  <MathText>{q.text}</MathText>
                </h2>

                {q.type === "mcq" ? (
                  <div className="mt-6 space-y-3" data-testid="mcq-options">
                    {(q.options || []).map((opt, i) => {
                      const selected = answers[q.id]?.selected_index === i;
                      return (
                        <button
                          key={`${q.id}-opt-${i}`}
                          type="button"
                          onClick={() => setAnswer(q.id, { selected_index: i })}
                          className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-center gap-3 ${
                            selected
                              ? "bg-indigo-50 border-indigo-500 text-slate-900"
                              : "bg-white border-slate-200 hover:border-slate-300"
                          }`}
                          data-testid={`option-${i}`}
                        >
                          <span
                            className={`w-6 h-6 grid place-items-center rounded-full border text-xs font-bold shrink-0 ${
                              selected ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-300 text-slate-500"
                            }`}
                          >
                            {String.fromCharCode(65 + i)}
                          </span>
                          <span className="text-sm"><MathText>{opt}</MathText></span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <textarea
                    value={answers[q.id]?.text_answer || ""}
                    onChange={(e) => setAnswer(q.id, { text_answer: e.target.value })}
                    placeholder="Type your answer here…"
                    rows={8}
                    className="mt-6 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition"
                    data-testid="descriptive-input"
                  />
                )}

                <div className="mt-8 flex items-center justify-between">
                  <button
                    onClick={() => setCurrent((c) => Math.max(0, c - 1))}
                    disabled={current === 0}
                    className="btn-ghost text-sm disabled:opacity-50"
                    data-testid="prev-question"
                  >
                    <ChevronLeft className="w-4 h-4" /> Previous
                  </button>
                  {current === questions.length - 1 ? (
                    <button onClick={doSubmit} disabled={busy} className="btn-primary text-sm" data-testid="submit-final">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Submit exam
                    </button>
                  ) : (
                    <button
                      onClick={() => setCurrent((c) => Math.min(questions.length - 1, c + 1))}
                      className="btn-primary text-sm"
                      data-testid="next-question"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          {err && <p className="mt-3 text-sm text-rose-600" data-testid="exam-error">{err}</p>}
        </main>
      </div>
    </div>
  );
}
